import { Box, TextInput, Typography } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { UserDetail } from "@dealer-center/ddc-pages-configuration-interface";
import { FIELD_GRID, FULL_WIDTH_FIELD } from "./fieldGrid";
import type { UserInfoEdits } from "./useUserInfoEdits";

export interface ContactMethodsProps {
  summary: UserDetail;
  edits: UserInfoEdits;
}

/**
 * FR-014's six fields: Public Email, Dealer.com Contact Email, Home #, Work #, Mobile #, Fax #.
 *
 * **The two email addresses are distinct and MUST NOT be merged into one field** (FR-014) — they
 * serve different purposes, and `email2` is empty for most users, which is exactly what makes
 * merging them look harmless.
 *
 * None of these six is required, so none carries a required indicator, and none carries an
 * "(optional)" tag either (FR-020). Four of the six come back as empty strings for the reference
 * subject; an empty value renders as an **empty field** — not a hidden row, not an omitted field, and
 * not placeholder text a user could mistake for a stored value (Story 1 scenario 3).
 *
 * **Every field is editable** as of 2026-08-31, and every field carries an explicit `id` so its
 * `<label for>` resolves — see the notes in `GeneralInformation` for both, including why FR-052's
 * "non-editable" bullet no longer holds and why FR-021's validation is still deferred.
 */
export const ContactMethods = ({ summary, edits }: ContactMethodsProps) => (
  <Box component={"section"} data-testid={"contact-methods"}>
    <Typography tag={"h2"} variant={"h4"}>
      {Locale.getText("CONTACT_METHODS") as string}
    </Typography>

    {/*
     * The two email addresses each take their own row; the four phone numbers pair up 2-up.
     * An address is a long value and its label is long too ("Dealer.com Contact Email"), so at half
     * width both truncate — and side by side the two similar-looking fields invite exactly the
     * confusion FR-014 warns about, where they read as one field duplicated rather than two distinct
     * addresses. Phone numbers are short and their labels are one word, so they pair cleanly.
     */}
    <Box sx={FIELD_GRID}>
      <TextInput
        data-testid={"field-public-email"}
        id={"myinfo-public-email"}
        label={Locale.getText("PUBLIC_EMAIL") as string}
        name={"email"}
        sx={FULL_WIDTH_FIELD}
        type={"email"}
        value={edits.valueOf(summary, "email")}
        onChange={event => edits.setField("email", event.target.value)}
      />
      <TextInput
        data-testid={"field-ddc-email"}
        id={"myinfo-ddc-email"}
        label={Locale.getText("DDC_CONTACT_EMAIL") as string}
        name={"email2"}
        sx={FULL_WIDTH_FIELD}
        type={"email"}
        value={edits.valueOf(summary, "email2")}
        onChange={event => edits.setField("email2", event.target.value)}
      />
      <TextInput
        data-testid={"field-home-phone"}
        id={"myinfo-home-phone"}
        label={Locale.getText("F_HOME") as string}
        name={"homePhone"}
        type={"tel"}
        value={edits.valueOf(summary, "homePhone")}
        onChange={event => edits.setField("homePhone", event.target.value)}
      />
      <TextInput
        data-testid={"field-work-phone"}
        id={"myinfo-work-phone"}
        label={Locale.getText("F_WORK") as string}
        name={"workPhone"}
        type={"tel"}
        value={edits.valueOf(summary, "workPhone")}
        onChange={event => edits.setField("workPhone", event.target.value)}
      />
      <TextInput
        data-testid={"field-mobile-phone"}
        id={"myinfo-mobile-phone"}
        label={Locale.getText("F_MOBILE") as string}
        name={"mobile"}
        type={"tel"}
        value={edits.valueOf(summary, "mobile")}
        onChange={event => edits.setField("mobile", event.target.value)}
      />
      <TextInput
        data-testid={"field-fax"}
        id={"myinfo-fax"}
        label={Locale.getText("F_FAX") as string}
        name={"fax"}
        type={"tel"}
        value={edits.valueOf(summary, "fax")}
        onChange={event => edits.setField("fax", event.target.value)}
      />
    </Box>
  </Box>
);
